/**
 * This row keeps paid-media trend and engagement visuals aligned while the
 * trend range remains independent from the report filter date range.
 */
import React from 'react';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import { useTranslation } from 'src/arb-wrapper';
import { HorizontalBarChart } from 'src/components/paid-media-dashboard/charts/HorizontalBarChart';
import { MultiSeriesLineChart } from 'src/components/paid-media-dashboard/charts/MultiSeriesLineChart';
import type { PaidMediaInsightsTrend } from 'src/store/leela-api';
import {
  DATE_PRESETS,
  DEFAULT_PRESET,
  type DatePreset,
} from 'src/utils/datePresets';
import { formatCompactNumber } from 'src/utils/formatCompactNumber';
import { RefreshLoader } from './RefreshLoader';
import { buildTrendSeries } from './trend-series';

const CHART_MIN_HEIGHT = 480;
const CHART_MIN_WIDTH = 480;
const TREND_PRESET_VALUES: readonly TrendPresetValue[] = [
  'last7',
  'last14',
  'last30',
];
const TREND_PRESET_LABEL_KEYS: Record<TrendPresetValue, string> = {
  last7: 'paid_media_trend_past_7_days',
  last14: 'paid_media_trend_past_14_days',
  last30: 'paid_media_trend_past_month',
};
const CHART_LAYOUT_STYLE: React.CSSProperties = {
  display: 'flex',
  flex: `1 1 ${CHART_MIN_WIDTH}px`,
  minHeight: CHART_MIN_HEIGHT,
  minWidth: 0,
  position: 'relative',
  width: '100%',
};

export type TrendPresetValue = 'last7' | 'last14' | 'last30';

interface TrendDatePreset extends DatePreset {
  readonly value: TrendPresetValue;
}

const isTrendPresetValue = (value: string): value is TrendPresetValue =>
  TREND_PRESET_VALUES.some((candidate) => candidate === value);

const TREND_DATE_PRESETS: TrendDatePreset[] = DATE_PRESETS.flatMap((preset) =>
  isTrendPresetValue(preset.value)
    ? [{ ...preset, value: preset.value }]
    : []);

export const getDefaultTrendPreset = (): TrendPresetValue =>
  isTrendPresetValue(DEFAULT_PRESET)
    ? DEFAULT_PRESET
    : TREND_PRESET_VALUES[0];

export const getTrendRange = (
  trendPreset: TrendPresetValue,
): { startDate: string; endDate: string } => {
  const preset = TREND_DATE_PRESETS.find(({ value }) => value === trendPreset) ??
    TREND_DATE_PRESETS[0];
  return preset.getRange();
};

interface TrendPresetSelectProps {
  readonly value: TrendPresetValue;
  readonly onChange: (value: TrendPresetValue) => void;
}

const TrendPresetSelect: React.FC<TrendPresetSelectProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <Select
      data-testid="paid-media-trend-date-preset"
      label={t('paid_media_trend_range_label')}
      size="small"
      value={value}
      width={150}
      onChange={(nextValue: string) => {
        if (isTrendPresetValue(nextValue)) onChange(nextValue);
      }}
    >
      {TREND_DATE_PRESETS.map((preset) => (
        <SelectOption
          key={preset.value}
          label={t(TREND_PRESET_LABEL_KEYS[preset.value])}
          value={preset.value}
        />
      ))}
    </Select>
  );
};

export interface InsightsChartsProps {
  readonly engagementData: Array<{ label: string; value: number }>;
  readonly isLoading: boolean;
  readonly showRefreshLoader: boolean;
  readonly trend?: PaidMediaInsightsTrend;
  readonly trendPreset: TrendPresetValue;
  readonly onTrendPresetChange: (value: TrendPresetValue) => void;
}

interface TrendChartPanelProps {
  readonly isLoading: boolean;
  readonly showRefreshLoader: boolean;
  readonly trend?: PaidMediaInsightsTrend;
  readonly trendPreset: TrendPresetValue;
  readonly onTrendPresetChange: (value: TrendPresetValue) => void;
}

const TrendChartPanel: React.FC<TrendChartPanelProps> = ({
  isLoading,
  showRefreshLoader,
  trend,
  trendPreset,
  onTrendPresetChange,
}) => {
  const { t } = useTranslation();
  const currency = trend?.currency ?? '';
  const series = buildTrendSeries(trend?.points ?? [], currency, {
    clicks: t('paid_media_insights_clicks'),
    spend: t('paid_media_insights_spend', { currency }),
  });

  return (
    <div data-testid="paid-media-trend-layout" style={CHART_LAYOUT_STYLE}>
      <MultiSeriesLineChart
        headerAction={(
          <TrendPresetSelect
            value={trendPreset}
            onChange={onTrendPresetChange}
          />
        )}
        isLoading={isLoading}
        minHeight={CHART_MIN_HEIGHT}
        series={series}
        title={t('paid_media_insights_trend')}
      />
      {showRefreshLoader && (
        <RefreshLoader testId="paid-media-trend-refresh-loader" />
      )}
    </div>
  );
};

interface EngagementChartPanelProps {
  readonly data: Array<{ label: string; value: number }>;
  readonly isLoading: boolean;
  readonly showRefreshLoader: boolean;
}

const EngagementChartPanel: React.FC<EngagementChartPanelProps> = ({
  data,
  isLoading,
  showRefreshLoader,
}) => {
  const { t } = useTranslation();

  return (
    <div data-testid="paid-media-engagement-layout" style={CHART_LAYOUT_STYLE}>
      <HorizontalBarChart
        data={data}
        isLoading={isLoading}
        minHeight={CHART_MIN_HEIGHT}
        title={t('paid_media_insights_engagement')}
        valueFormatter={(value: number) => formatCompactNumber(value)}
      />
      {showRefreshLoader && (
        <RefreshLoader testId="paid-media-engagement-refresh-loader" />
      )}
    </div>
  );
};

export const InsightsCharts: React.FC<InsightsChartsProps> = ({
  engagementData,
  isLoading,
  showRefreshLoader,
  trend,
  trendPreset,
  onTrendPresetChange,
}) => (
  <Row
    alignmentVertical="stretch"
    data-testid="paid-media-insights-chart-row"
    spacing="300"
    wrap="down"
  >
    <TrendChartPanel
      isLoading={isLoading}
      showRefreshLoader={showRefreshLoader}
      trend={trend}
      trendPreset={trendPreset}
      onTrendPresetChange={onTrendPresetChange}
    />
    <EngagementChartPanel
      data={engagementData}
      isLoading={isLoading}
      showRefreshLoader={showRefreshLoader}
    />
  </Row>
);
